import { useCallback, useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../../providers/firebase/firebase';
import type { GateMode } from '../../../types/gates';

interface UseGateModeResult {
    /** null while loading, or when the control doc / mode field is absent
     * or holds a value other than exactly 'auto' | 'gated' (a foreign
     * writer) — GATE_UI.md §7: never surface an unknown mode as real. */
    mode: GateMode | null;
    loading: boolean;
    setMode: (mode: GateMode) => Promise<void>;
    saving: boolean;
}

function readMode(data: Record<string, unknown> | undefined): GateMode | null {
    const mode = data?.mode;
    return mode === 'auto' || mode === 'gated' ? mode : null;
}

/**
 * Live view + audited toggle write for the account-level gate control doc
 * `gates/{account_id}` (design/GATE.md §3, design/GATE_UI.md §2.2).
 *
 * `setMode` merge-writes exactly `{mode, mode_changed_by, mode_changed_at}`
 * — an RFC3339 string timestamp, NEVER serverTimestamp() (bogg parses it
 * with parse_from_rfc3339) — so bogg's `updated_at` seed touch is never
 * clobbered.
 */
export function useGateMode(accountId: string | null): UseGateModeResult {
    const [mode, setModeState] = useState<GateMode | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!accountId) {
            setModeState(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        const unsub = onSnapshot(
            doc(db, `gates/${accountId}`),
            snap => {
                setModeState(readMode(snap.data()));
                setLoading(false);
            },
            () => {
                setModeState(null);
                setLoading(false);
            }
        );
        return () => unsub();
    }, [accountId]);

    const setMode = useCallback(
        async (next: GateMode) => {
            if (!accountId) return;
            const uid = getAuth().currentUser?.uid;
            if (!uid) throw new Error('not_signed_in');
            setSaving(true);
            try {
                await setDoc(
                    doc(db, `gates/${accountId}`),
                    {
                        mode: next,
                        mode_changed_by: uid,
                        mode_changed_at: new Date().toISOString(),
                    },
                    { merge: true }
                );
            } finally {
                setSaving(false);
            }
        },
        [accountId]
    );

    return { mode, loading, setMode, saving };
}
